import { useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import { useBulkDownloadCustomerDocuments } from "lib/hooks/api/customer_documents/useBulkDownloadCustomerDocuments";
import { CustomerDocumentDetails } from "lib/hooks/api/customer_documents/useCustomerDocuments";
import { pluralise } from "lib/utils";
import { DeleteCustomerDocumentModal } from "./modals/DeleteCustomerDocumentModal";

type Props = {
  selectedDocuments: CustomerDocumentDetails[];
  onClearSelection: () => void;
};

export default function ContentLibraryActionBar({ selectedDocuments, onClearSelection }: Props) {
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const { mutate: downloadDocuments, isLoading: isDownloading } = useBulkDownloadCustomerDocuments({
    onSuccess: async (_data, variables) => {
      const downloadedFiles = variables.documents;

      if (downloadedFiles.length === 1) {
        toast.success(`Downloaded document ${downloadedFiles[0].fileName}`);
      } else {
        toast.success(`Downloaded ${downloadedFiles.length} documents`);
      }

      onClearSelection(); // Clear selection after deletion
    },
    onError: (_err, vars) =>
      toast.error(`Failed to download ${pluralise(vars.documents.length, "document")}`),
  });

  return (
    <>
      <Container aria-label="Content library bulk action bar">
        <Flex gap={12} alignItems="center">
          <Text color="white">{`${pluralise(selectedDocuments.length, "file")} selected:`}</Text>
          <ActionButtonWrapper>
            <Button iconProps={{ size: 16, color: "white" }} icon="stars2">
              Ask Stotles AI
            </Button>
          </ActionButtonWrapper>
          <Button
            aria-label="Download selected documents"
            icon="download01"
            buttonType="tertiary"
            buttonStyle="inverse"
            loading={isDownloading}
            onClick={() =>
              downloadDocuments({
                documents: selectedDocuments,
              })
            }
          />
          <Button
            aria-label="Delete selected documents"
            icon="trash01"
            buttonType="tertiary"
            buttonStyle="inverse"
            onClick={() => setDeleteModalOpen(true)}
          />
        </Flex>
        <Flex gap={8} alignItems="center">
          <Button aria-label="Close" icon="xClose" onClick={onClearSelection} />
        </Flex>
      </Container>
      <DeleteCustomerDocumentModal
        selectedDocuments={selectedDocuments}
        isOpen={isDeleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onSuccess={() => {
          onClearSelection();
          setDeleteModalOpen(false);
        }}
      />
    </>
  );
}

const Container = styled(Flex)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "center",
  height: 48,
  padding: 16,
  backgroundColor: theme.colors.sysPrimaryDefault,
  borderRadius: 4,
  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.12)",
}));

const ActionButtonWrapper = styled.div(({ theme }) => ({
  border: `1px solid ${theme.colors.sysTextWhite}`,
  borderRadius: 4,
  position: "relative",
}));
